/**
 * Pole „Profil průvodce" karty člověka (vize F6.5, F6.9): výběr průvodce, nabídka česky
 * abecedně, neaktivní s příponou. Čistě prezentační; průvodce načítá stránka
 * (`useGuideLookup`).
 *
 * Volba „Bez profilu průvodce" vrací `null`, ne `""`: `destinationTeamCreateSchema.guideId`
 * je `min(1)` bez `blankToUndefined` a prázdný řetězec by uložení shodil na 400.
 */
import { useTranslation } from "react-i18next";
import { sortCzech } from "@ckgo2/shared";
import type { GuideLookupItem } from "./queries";

interface GuideProfileFieldProps {
  id: string;
  value: string | null | undefined;
  /** `undefined` = průvodci se načítají. */
  guides: readonly GuideLookupItem[] | undefined;
  onChange: (id: string | null) => void;
}

export function GuideProfileField({ id, value, guides, onChange }: GuideProfileFieldProps) {
  const { t } = useTranslation();
  const sorted = sortCzech(guides ?? [], (g) => g.name);
  // Uložená hodnota, která v nabídce není (načítá se, nebo průvodce mezitím zmizel), zůstává
  // jako volba s id: `<select>` by ji jinak tiše ukázal jako prázdnou a další uložení by
  // profil zrušilo.
  const missing = value && !sorted.some((g) => g.id === value) ? value : null;
  const hintId = `${id}-hint`;

  return (
    <div>
      <label htmlFor={id} className="label">
        {t("people.edit.guideProfile")}
      </label>
      <select
        id={id}
        data-team-guide=""
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        aria-describedby={hintId}
        className="input"
      >
        <option value="">{t("people.edit.guideProfileNone")}</option>
        {missing && <option value={missing}>{missing}</option>}
        {sorted.map((guide) => (
          <option key={guide.id} value={guide.id}>
            {guide.isActive ? guide.name : `${guide.name} (${t("people.edit.inactive")})`}
          </option>
        ))}
      </select>
      <p id={hintId} className="mt-1 text-xs text-gray-500">
        {t("people.edit.guideProfileHint")}
      </p>
    </div>
  );
}
